/**
 * @file BaseScreenPageService.ts
 *
 * 底座屏显页面业务编排服务。
 * 这里只负责把“页面数组”转换成底层屏显 API 需要的名称、顺序和停留时间，
 * 不直接处理 HID 协议字节，也不负责素材上传。
 */
import type { BaseImageOrder, BaseScreenPageItem } from '../../../../types/base'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'

export interface BaseScreenPageTransport {
  /** 读取指定槽位在设备中保存的图片名称。 */
  getImageName(index: number): Promise<string>
  /** 写入指定槽位的图片名称。 */
  setImageName(index: number, name: string): Promise<void>
  /** 读取固件轮播配置：展示数量 imageNum + 轮播槽位 order。 */
  getImageOrder(): Promise<BaseImageOrder>
  /** 写入固件轮播配置：前 showNum 个 order 槽位会参与轮播。 */
  setImageOrder(showNum: number, order: number[] | Uint8Array): Promise<void>
  /** 读取每个槽位的轮播停留时间，数组下标就是槽位号。 */
  getImageTimes(): Promise<number[]>
  /** 写入指定槽位的轮播停留时间。 */
  setImageTime(index: number, seconds: number): Promise<void>
}

export interface BaseScreenPageServiceOptions {
  /** manifest 声明的物理屏显槽位总数。 */
  maxScreenSlots?: number
  /** manifest 声明的系统页面槽位，顺序也是固定播放顺序。 */
  systemSlots?: readonly number[]
}

/**
 * 屏显页面业务编排层。
 *
 * 设备协议把屏显拆成三组字段：
 * - imageName：每个槽位的图片名称。
 * - imageTimes：每个槽位的停留时间，数组下标对应槽位。
 * - imageOrder：轮播顺序，imageNum 表示 order 前 N 项参与展示。
 *
 * Web 业务维护 manifest 声明长度的页面数组，所以这里统一负责两种模型之间的转换。
 */
export class BaseScreenPageService {
  private readonly maxScreenSlots: number
  private readonly systemSlots: readonly number[]
  private readonly systemSlotSet: ReadonlySet<number>

  constructor(
    private readonly transport: BaseScreenPageTransport,
    options: BaseScreenPageServiceOptions = {},
  ) {
    this.maxScreenSlots = options.maxScreenSlots ?? 8
    if (!Number.isInteger(this.maxScreenSlots) || this.maxScreenSlots <= 0) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        'maxScreenSlots must be a positive integer',
      )
    }

    const systemSlots = options.systemSlots ?? []
    systemSlots.forEach((slot) => this.assertImageSlot(slot))
    if (new Set(systemSlots).size !== systemSlots.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'System screen slots must be unique')
    }
    this.systemSlots = [...systemSlots]
    this.systemSlotSet = new Set(systemSlots)
  }

  /**
   * 读取屏显页面业务列表。
   *
   * 返回 manifest 声明数量的页面，数组顺序就是 Web 层看到的播放顺序。
   */
  async getPages(): Promise<BaseScreenPageItem[]> {
    const snapshot = await this.readDeviceSnapshot()

    const result = snapshot.order.map((slot, index) => ({
      id: snapshot.names[slot] ?? '',
      slot,
      delay: snapshot.times[slot] ?? 3,
      order: index + 1,
      // 固件用 imageNum 表示 order 数组前 N 项参与轮播。
      show: this.systemSlotSet.has(slot) || index < snapshot.imageNum,
    }))

    console.log('getPages', result)

    return result
  }

  /**
   * 写入屏显页面业务列表。
   *
   * Web 只需要维护 pages 数组；这里负责翻译成固件真正需要的
   * setImageOrder / setImageTime / setImageName 调用。
   * 写入前会先读取设备当前值，仅更新发生变化的字段。
   */
  async setPages(pages: BaseScreenPageItem[]): Promise<void> {
    const normalizedPages = this.normalizeScreenPages(pages)
    const visiblePages = normalizedPages.filter((page) => page.show)
    if (visiblePages.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'At least one screen page must be shown')
    }

    // 固件只认“order 前 imageNum 项展示”，因此必须把展示项排在隐藏项之前。
    const orderedPages = [...visiblePages, ...normalizedPages.filter((page) => !page.show)]
    const targetImageNum = visiblePages.length
    const targetOrder = orderedPages.map((page) => page.slot)

    const snapshot = await this.readDeviceSnapshot()

    if (this.hasOrderChanged(snapshot, targetImageNum, targetOrder)) {
      await this.transport.setImageOrder(targetImageNum, targetOrder)
    }

    for (const page of orderedPages) {
      const currentDelay = snapshot.times[page.slot] ?? 3
      if (currentDelay !== page.delay) {
        await this.transport.setImageTime(page.slot, page.delay)
      }

      if (this.systemSlotSet.has(page.slot)) continue

      const currentName = snapshot.names[page.slot] ?? ''
      // 空 id 表示清空槽位名称，必须写回设备，才能让页面数组成为完整状态源。
      if (currentName !== page.id) {
        await this.transport.setImageName(page.slot, page.id)
      }
    }
  }

  /** 读取设备当前的轮播顺序、停留时间和槽位名称快照。 */
  private async readDeviceSnapshot(): Promise<{
    imageNum: number
    order: number[]
    times: number[]
    names: string[]
  }> {
    const [imageOrder, imageTimes, imageNames] = await Promise.all([
      this.transport.getImageOrder(),
      this.transport.getImageTimes(),
      Promise.all(
        Array.from({ length: this.maxScreenSlots }, (_, slot) => this.transport.getImageName(slot)),
      ),
    ])

    return {
      imageNum: this.clampVisibleCount(imageOrder.imageNum),
      order: this.normalizeScreenSlots(imageOrder.order),
      times: imageTimes,
      names: imageNames,
    }
  }

  /** 判断目标轮播顺序是否与设备当前顺序一致。 */
  private hasOrderChanged(
    snapshot: { imageNum: number; order: number[] },
    targetImageNum: number,
    targetOrder: number[],
  ): boolean {
    if (snapshot.imageNum !== targetImageNum) return true

    return targetOrder.some((slot, index) => snapshot.order[index] !== slot)
  }

  /**
   * 将固件 order 归一成 manifest 声明数量的不重复槽位。
   *
   * 固件恢复、异常数据或历史版本都可能返回重复/越界槽位。
   * Web 层只应该收到稳定的完整数组，所以这里过滤坏值并补齐缺失槽位。
   */
  private normalizeScreenSlots(order: number[]): number[] {
    const slots: number[] = []
    for (const slot of order) {
      if (
        Number.isInteger(slot) &&
        slot >= 0 &&
        slot < this.maxScreenSlots &&
        !slots.includes(slot)
      ) {
        slots.push(slot)
      }
    }
    // 补齐没出现在固件 order 里的槽位，保证 Web 仍能管理全部物理槽位。
    for (let slot = 0; slot < this.maxScreenSlots; slot++) {
      if (!slots.includes(slot)) {
        slots.push(slot)
      }
    }

    const customSlots = slots.filter((slot) => !this.systemSlotSet.has(slot))
    return [...this.systemSlots, ...customSlots].slice(0, this.maxScreenSlots)
  }

  /**
   * 校验并按业务顺位归一屏显页面列表。
   *
   * 这是 SDK 对外部输入的防线：写设备前先确保槽位唯一、数量固定、
   * 停留时间合法，避免把不可恢复的脏顺序写进固件。
   */
  private normalizeScreenPages(pages: BaseScreenPageItem[]): BaseScreenPageItem[] {
    if (!Array.isArray(pages) || pages.length !== this.maxScreenSlots) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `Screen pages must contain exactly ${this.maxScreenSlots} items`,
      )
    }

    const usedSlots = new Set<number>()
    const normalizedPages = pages.map((page) => {
      if (!page || typeof page !== 'object') {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Screen page item is invalid')
      }
      this.assertImageSlot(page.slot)
      if (usedSlots.has(page.slot)) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `Duplicate screen slot: ${page.slot}`)
      }
      usedSlots.add(page.slot)
      this.assertIntegerRange(page.delay, 3, 65535, 'screen page delay')

      const id = typeof page.id === 'string' ? page.id : ''

      return {
        id,
        slot: page.slot,
        delay: page.delay,
        // order 不合法时放到队尾，避免单个坏 order 破坏整体页面写入。
        order: Number.isFinite(page.order) ? Math.floor(page.order) : Number.MAX_SAFE_INTEGER,
        // 自定义槽位没有图片名称时不可参与轮播；系统页不依赖图片名称。
        show: this.systemSlotSet.has(page.slot) || (Boolean(page.show) && id.length > 0),
      }
    })

    const pagesBySlot = new Map(normalizedPages.map((page) => [page.slot, page]))
    const systemPages = this.systemSlots.map((slot) => pagesBySlot.get(slot)!)
    const customPages = normalizedPages
      .filter((page) => !this.systemSlotSet.has(page.slot))
      .sort((left, right) => left.order - right.order)

    return [...systemPages, ...customPages]
  }

  /** 固件偶发返回异常 imageNum 时，将它收敛到 Web 可理解的槽位范围。 */
  private clampVisibleCount(value: number): number {
    if (!Number.isFinite(value)) return 0
    return Math.min(this.maxScreenSlots, Math.max(0, Math.floor(value)))
  }

  /** 槽位是固件地址维度，越界会写到错误位置，必须严格校验。 */
  private assertImageSlot(slot: number): void {
    if (!Number.isInteger(slot) || slot < 0 || slot >= this.maxScreenSlots) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `screen slot must be an integer between 0 and ${this.maxScreenSlots - 1}`,
      )
    }
  }

  /** 设备停留时间协议只接受整数秒，提前拦截小数和越界值。 */
  private assertIntegerRange(value: number, min: number, max: number, label: string): void {
    if (!Number.isInteger(value) || value < min || value > max) {
      throw new DeviceError(
        DeviceErrorCode.INVALID_PARAM,
        `${label} must be an integer between ${min} and ${max}`,
      )
    }
  }
}
